// frontend/src/pages/admin/Issues.js
//
// The customer-reported problem worklist. /rentals/:id/report-issue has existed
// since the round that gave customers a channel that was not a mailto: link, but
// nothing ever displayed what came out of it — reports landed in a table no
// screen read from, which is worse than not offering the button at all.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '../../routing';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../i18n';
import { format } from 'date-fns';
import { AlertTriangle, RefreshCw, Phone, Save, Check, Eye, Activity as ActivityIcon } from 'lucide-react';
import { API_BASE } from '../../config';
// Shared so that a rental's status reads the same word here as it does on the
// rentals table — the locale keys are camelCase while the API sends snake_case,
// which is exactly the mismatch this map exists to absorb.
import { statusLabel } from './statusLabels';
import theme from '../../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

const PAGE_SIZE = 25;

const STATUS_COLORS = {
  open:         theme.state.pending,
  acknowledged: theme.state.info,
  resolved:     theme.state.success,
};

// A locker that will not open is someone standing in front of a machine right
// now; "quality is not what I expected" can wait until tomorrow. The tint is the
// only thing that separates those two at a glance in a mixed list.
const CATEGORY_ACCENTS = {
  locker_problem:     theme.redBrick,
  cannot_return:      theme.redBrick,
  damaged_on_arrival: theme.amber,
  wrong_item:         theme.amber,
  quality:            theme.grey600,
  other:              theme.grey600,
};

// Photos come back as `/uploads/...`, which a browser resolves against whatever
// origin served the page. Under nginx that is the backend, but the Capacitor
// build has no such origin — the page is a file inside the app bundle — so an
// unprefixed path silently resolves to nothing. Derive the origin from the one
// place the API location is configured rather than hardcoding a second copy.
const UPLOAD_BASE = API_BASE.replace(/\/api\/?$/, '');
const photoUrl = (u = '') =>
  /^https?:\/\//i.test(u) ? u : `${UPLOAD_BASE}${u.startsWith('/') ? '' : '/'}${u}`;

// The interceptor flattens the error body, so `error` is the machine code and
// `message` the sentence worth showing — `err.response.data` is not a thing
// here, a previous round had to unpick exactly that mistake. Anything without
// `error` is a raw axios failure whose own `.message` reads "Request failed with
// status code 500", which is not something to put in front of an operator.
const serverText = (err, fallback) =>
  (err && typeof err === 'object' && err.error) ? (err.message || err.error) : fallback;

export default function AdminIssues() {
  const { t } = useTranslation();
  // Greek admins were reading English month names because date-fns defaults to
  // en-US when no locale is handed to it.
  const dateLocale = getDateLocale();

  const [issues, setIssues]         = useState([]);
  const [count, setCount]           = useState(0);
  const [openCount, setOpenCount]   = useState(null);
  const [statusFilter, setStatus]   = useState('open');
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [busyId, setBusyId]         = useState(null);
  // Note drafts live outside the row objects so that a reload of the list — or
  // another admin's edit arriving — cannot wipe half-typed text.
  const [drafts, setDrafts]         = useState({});
  // Switching filter twice quickly leaves two loads in flight, and they do not
  // answer in order. An older answer landing last showed resolved reports under
  // the 'open' tab — on this screen that reads as "nobody has a problem".
  const loadSeq = useRef(0);

  useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // A narrower filter may not have the page currently being shown.
  useEffect(() => { setPage(1); }, [statusFilter]);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    const seq = ++loadSeq.current;
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    api.get(`/rentals/admin/issues?${params}`)
      .then(d => {
        if (seq !== loadSeq.current) return; // a newer filter is already loading
        setIssues(Array.isArray(d?.issues) ? d.issues : []);
        setCount(Number(d?.count) || 0);
      })
      // An outage rendered as an empty list reads as "no customer has a problem",
      // which is the single most dangerous thing this screen could claim.
      .catch(err => {
        if (seq !== loadSeq.current) return;
        setLoadError(serverText(err, t('admin.issues.loadFailedDetail')));
      })
      .finally(() => { if (seq === loadSeq.current) setLoading(false); });
  }, [page, statusFilter, t]);

  // The open count has to survive being on the 'resolved' tab, so it is its own
  // one-row query rather than something counted out of the visible list.
  const loadOpenCount = useCallback(() => {
    api.get('/rentals/admin/issues?status=open&limit=1')
      .then(d => setOpenCount(Number(d?.count) || 0))
      .catch(() => setOpenCount(null));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadOpenCount(); }, [loadOpenCount]);

  const patchIssue = async (issue, body, successMessage) => {
    setBusyId(issue.id);
    try {
      const updated = await api.patch(`/rentals/issues/${issue.id}`, body);
      // The row is updated in place rather than dropped, even when its new status
      // no longer matches the active filter: having a report vanish the instant it
      // is acknowledged makes it impossible to then add the note explaining why.
      setIssues(prev => prev.map(i => (i.id === issue.id ? { ...i, ...updated } : i)));
      toast.success(successMessage);
      loadOpenCount();
    } catch (err) {
      toast.error(serverText(err, t('admin.issues.updateFailed')), { duration: 8000 });
    } finally {
      setBusyId(null);
    }
  };

  const categoryLabel = (c) => t(`admin.issues.category.${c}`, { defaultValue: c });
  const statusText    = (s) => t(`admin.issues.status.${s}`, { defaultValue: s });

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      <div className="admin-main" style={{ padding: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 28 }}>
          <div>
            <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, margin: 0 }}>{t('admin.issues.title')}</h1>
            <p style={{ color: theme.grey400, margin: '4px 0 0' }}>{t('admin.issues.subtitle')}</p>
          </div>
          {/* The number a shift starts by looking at. */}
          <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 10, padding: '14px 24px', minWidth: 120, textAlign: 'center' }}>
            <div style={{ fontFamily: theme.font.display, fontSize: 34, lineHeight: 1, color: openCount ? theme.redBrick : theme.greenDeep }}>
              {openCount === null ? '—' : openCount}
            </div>
            <div style={{ fontSize: 11, color: theme.grey400, marginTop: 6, letterSpacing: '0.06em' }}>{t('admin.issues.statOpen')}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24, alignItems: 'center' }}>
          {['open', 'acknowledged', 'resolved', 'all'].map(s => (
            <button key={s} onClick={() => setStatus(s)} style={{
              padding: '8px 18px',
              border: statusFilter === s ? 'none' : `1px solid ${theme.sandMid}`,
              background: statusFilter === s ? theme.ink : 'transparent',
              color: statusFilter === s ? theme.white : theme.grey600,
              borderRadius: 100, fontSize: 11, letterSpacing: '0.06em', cursor: 'pointer', fontFamily: theme.font.body, whiteSpace: 'nowrap',
            }}>
              {s === 'all' ? t('admin.issues.filterAll') : statusText(s)}
            </button>
          ))}
          <button onClick={() => { load(); loadOpenCount(); }} title={t('admin.issues.refresh')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', padding: '8px 16px', border: `1px solid ${theme.sand}`, background: theme.white, borderRadius: 4, cursor: 'pointer', fontSize: 12, color: theme.grey600, fontFamily: theme.font.body }}>
            <RefreshCw size={13} /> {t('admin.issues.refresh')}
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 100, color: theme.gold, fontFamily: theme.font.display, fontSize: 24 }}>{t('admin.issues.loading')}</div>
        ) : loadError ? (
          <div style={{ background: theme.white, border: `1px solid ${theme.redWash}`, borderRadius: 10, padding: 48, textAlign: 'center' }}>
            <AlertTriangle size={26} style={{ color: theme.redBrick }} />
            <div style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.ink, margin: '10px 0 6px' }}>{t('admin.issues.loadFailedTitle')}</div>
            <p style={{ color: theme.grey400, margin: '0 0 20px', fontSize: 13 }}>{loadError}</p>
            <button onClick={load} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: theme.ink, color: theme.white, border: 'none', padding: '12px 26px', borderRadius: 8, cursor: 'pointer', fontSize: 12, letterSpacing: '0.1em', fontFamily: theme.font.body }}>
              <RefreshCw size={13} /> {t('admin.issues.retry')}
            </button>
          </div>
        ) : issues.length === 0 ? (
          <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 10, padding: 72, textAlign: 'center' }}>
            <div style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.grey400 }}>
              {statusFilter === 'open' ? t('admin.issues.emptyOpen') : t('admin.issues.empty')}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {issues.map(issue => {
              const sc = STATUS_COLORS[issue.status] || { bg: theme.grey100, color: theme.grey600 };
              const accent = CATEGORY_ACCENTS[issue.category] || theme.grey600;
              const draft = drafts[issue.id] ?? (issue.admin_notes || '');
              const busy = busyId === issue.id;
              return (
                <div key={issue.id} style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: '20px 24px' }}>

                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: theme.font.display, fontSize: 22, color: theme.ink }}>{categoryLabel(issue.category)}</span>
                        <span style={{ background: sc.bg, color: sc.color, padding: '3px 12px', borderRadius: 100, fontSize: 10, letterSpacing: '0.05em', fontWeight: 500 }}>
                          {statusText(issue.status)}
                        </span>
                      </div>
                      <div style={{ color: theme.grey400, fontSize: 12, marginTop: 6 }}>
                        {issue.created_at ? format(new Date(issue.created_at), 'd MMM yyyy, HH:mm', { locale: dateLocale }) : '—'}
                        {issue.product_name && <> · <span style={{ color: theme.grey600 }}>{issue.product_name}</span></>}
                        {issue.rental_status && <> · {statusLabel(t, issue.rental_status)}</>}
                      </div>
                    </div>

                    {/* Who to call. A locker problem is a person standing at the
                        machine — the phone number is the entire point of this
                        block, so it is a dialable link and not a text column. */}
                    <div style={{ textAlign: 'right', fontSize: 13 }}>
                      <div style={{ color: theme.ink, fontWeight: 500 }}>{issue.full_name || '—'}</div>
                      {issue.email && <div style={{ color: theme.grey400, fontSize: 12 }}>{issue.email}</div>}
                      {issue.phone && (
                        <a href={`tel:${issue.phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, color: theme.blue, textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>
                          <Phone size={14} /> {issue.phone}
                        </a>
                      )}
                    </div>
                  </div>

                  <p style={{ color: theme.ink700, fontSize: 14, lineHeight: 1.75, margin: '16px 0 0', whiteSpace: 'pre-wrap' }}>{issue.description}</p>

                  {issue.photo_urls?.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                      {issue.photo_urls.map((u, i) => (
                        // Opened full size in a tab: a thumbnail of a damaged weft
                        // decides nothing, and this is the evidence a deposit
                        // deduction will later have to stand on.
                        <a key={i} href={photoUrl(u)} target="_blank" rel="noopener noreferrer" title={t('admin.issues.viewPhoto')}
                          style={{ display: 'block', lineHeight: 0, border: `1px solid ${theme.sand}`, borderRadius: 4, overflow: 'hidden' }}>
                          <img src={photoUrl(u)} alt="" style={{ width: 84, height: 84, objectFit: 'cover', display: 'block' }} />
                        </a>
                      ))}
                    </div>
                  )}

                  <div style={{ marginTop: 18, borderTop: `1px solid ${theme.creamBone}`, paddingTop: 16 }}>
                    <label style={{ display: 'block', fontSize: 11, color: theme.grey500, letterSpacing: '0.08em', marginBottom: 6 }}>{t('admin.issues.notesLabel')}</label>
                    <textarea
                      value={draft}
                      onChange={e => setDrafts(prev => ({ ...prev, [issue.id]: e.target.value }))}
                      rows={2}
                      placeholder={t('admin.issues.notesPlaceholder')}
                      style={{ width: '100%', padding: '10px 12px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 13, fontFamily: theme.font.body, boxSizing: 'border-box', resize: 'vertical' }}
                    />

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
                      <button disabled={busy} onClick={() => patchIssue(issue, { admin_notes: draft }, t('admin.issues.notesSaved'))}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1, fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body }}>
                        <Save size={13} /> {t('admin.issues.saveNotes')}
                      </button>

                      {issue.status === 'open' && (
                        <button disabled={busy} onClick={() => patchIssue(issue, { status: 'acknowledged', admin_notes: draft }, t('admin.issues.acknowledgedToast'))}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: theme.blue, color: theme.white, border: 'none', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1, fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body }}>
                          <Eye size={13} /> {t('admin.issues.acknowledge')}
                        </button>
                      )}

                      {issue.status !== 'resolved' && (
                        <button disabled={busy} onClick={() => patchIssue(issue, { status: 'resolved', admin_notes: draft }, t('admin.issues.resolvedToast'))}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: theme.green, color: theme.white, border: 'none', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1, fontSize: 12, letterSpacing: '0.06em', fontFamily: theme.font.body }}>
                          <Check size={13} /> {t('admin.issues.resolve')}
                        </button>
                      )}

                      {issue.resolved_at && (
                        <span style={{ fontSize: 12, color: theme.green }}>
                          {t('admin.issues.resolvedAt', { date: format(new Date(issue.resolved_at), 'd MMM yyyy, HH:mm', { locale: dateLocale }) })}
                        </span>
                      )}

                      {issue.rental_id && (
                        <Link to={`/admin/activity?entity_id=${issue.rental_id}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', color: theme.grey500, textDecoration: 'none', fontSize: 12 }}>
                          <ActivityIcon size={13} /> {t('admin.issues.viewActivity')}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !loadError && count > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, fontSize: 13, color: theme.grey500 }}>
            <span>
              {t('admin.issues.pagination', {
                from: (page - 1) * PAGE_SIZE + 1,
                to: Math.min(page * PAGE_SIZE, count),
                total: count,
              })}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.issues.prev')}
              </button>
              <span style={{ padding: '8px 12px', background: theme.ink, color: theme.white, borderRadius: 4, fontSize: 12 }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= count}
                style={{ padding: '8px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, background: theme.white, cursor: page * PAGE_SIZE >= count ? 'not-allowed' : 'pointer', opacity: page * PAGE_SIZE >= count ? 0.4 : 1, fontFamily: theme.font.body, fontSize: 12 }}>
                {t('admin.issues.next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
